// **Builds zur Auswahl** — rein, getestet (releases.test.ts). Die Liste kommt
// vom Server (`GET /releases`, neueste zuerst); gewählt wird nur aus ihr,
// getippt wird nichts.

import type { PlatformUpdateStatus, Release } from "../api/types";
import type { SelectOptions } from "../ui/select-menu";

export const BUILD_RE = /^\d+\.\d+\.\d+-[0-9a-f]{8}$/;

const pad = (n: number) => String(n).padStart(2, "0");

/** „09.10. 18:20" (Ortszeit) — kurz genug für eine Zeile im Menü. */
export function releaseWhen(iso: string): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return "";
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}. ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function shorten(s: string, max: number): string {
  const t = s.trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** „1.20.0-90605e6a · v1.20.0 · 09.10. 18:20 · ci: … · läuft" */
export function releaseLabel(r: Release, running: string | null): string {
  const parts = [r.build, r.release, releaseWhen(r.created), shorten(r.title, 70)].filter(Boolean);
  if (r.build === running) parts.push("läuft");
  return parts.join(" · ");
}

/**
 * Die Einträge des Menüs. Der laufende Build steht auch dann darin, wenn er
 * aus der Liste der neuesten herausgefallen ist — „Erneut ausrollen" soll
 * immer gehen.
 */
export function releaseOptions(releases: Release[], running: string | null): SelectOptions {
  const out: Array<readonly [string, string]> = releases.map((r) => [r.build, releaseLabel(r, running)] as const);
  if (running && BUILD_RE.test(running) && !releases.some((r) => r.build === running)) {
    out.push([running, `${running} · läuft (älter als die Liste)`]);
  }
  return out;
}

/** Vorauswahl: der neueste Build. */
export function defaultBuild(releases: Release[]): string {
  return releases[0]?.build ?? "";
}

export type UpdatePhase = "idle" | "pulling" | "helper" | "restarting" | "done" | "failed";

/**
 * Wo die Aktualisierung der Plattform steht. `unreachable`: die Abfrage
 * scheiterte — mitten in einer Aktualisierung heißt das „startet neu".
 */
export function updatePhase(s: PlatformUpdateStatus | undefined, unreachable: boolean, watching: boolean): UpdatePhase {
  if (unreachable) return watching ? "restarting" : "idle";
  if (!s) return "idle";
  if (s.pulling?.state === "running") return "pulling";
  if (s.pulling?.state === "failed") return "failed";
  if (s.helper?.running) return "helper";
  if (s.helper && s.helper.exit_code !== null) {
    if (s.helper.exit_code !== 0) return "failed";
    // Fertig ist es erst, wenn DIESE Plattform auf dem neuen Build läuft.
    if (s.helper.build && s.helper.build === s.current) return "done";
    return watching ? "restarting" : "idle";
  }
  if (s.pulling?.state === "done") return "helper";
  return "idle";
}

/** Solange es sich bewegt, wird nachgefragt. */
export function updateMoving(phase: UpdatePhase): boolean {
  return phase === "pulling" || phase === "helper" || phase === "restarting";
}

/** Das Protokoll, das gerade zählt: das des Helfers, sonst das des Ziehens. */
export function updateLog(s: PlatformUpdateStatus | undefined): string[] {
  if (!s) return [];
  if (s.pulling && (s.pulling.state !== "done" || !s.helper?.log.length)) return s.pulling.log;
  return s.helper?.log ?? [];
}
